import React, { useEffect, useMemo, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ReentryProjectionDTO, TargetSchedule, UPDATE_TYPE } from "../../../../models/EnrollmentUpdateDTO";
import { cursoCorto } from "../../../../utils/cursoCorto";
import BuscadorHorarios, { DetalleHorario, FiltroCasillas, filaDeDestino } from "../../../../components/horarios/buscadorHorarios";
import { BotonWhatsappAlumno, useVeWappChat } from "../../../../components/whatsapp/whatsappAlumno";
import EnvioMasivo from "../../../../components/whatsapp/envioMasivo";
import InscripcionReingreso, { promoDeReingreso } from "../../../../components/acuerdos/inscripcionReingreso";
import S from "./strings";
import { TabProps, dinero, errorText, fechaCorta, nombreHorario } from "./helpers";
import { FloatingListDTO, FloatingRow, FloatingView } from "../../../../models/EnrollmentUpdateDTO";
import { FiltrosFlotantes, SIN_FILTROS, cargarFlotantes, consulta, contarVistas, hayFiltros, marcarContactada, todasLasFilas } from "./flotantesService";
import { useSelector } from "react-redux";
import { RootState } from "../../../../globals";

const Aperturas: React.FC<{ list: TargetSchedule[] }> = ({ list }) => list.length === 0
	? <span className="wau-pastilla oro">{S.noneOpen}</span>
	: <>{list.slice(0, 3).map((s) => (
		<span key={"o" + s.id} className={"wau-pastilla" + (s.isFull ? "" : " acento")}>{s.sessions || s.name} <b>{Math.max(0, s.limit - s.enrolled)}</b></span>
	))}{list.length > 3 ? <span className="wau-sec">+{list.length - 3}</span> : null}</>;

/** Ubicar: elige el horario con el Buscador de Horarios, ve la proyección de sesiones y lo manda a Por Aplicar (o lo aplica, si es Caja). */
const UbicarModal: React.FC<{ campusId: number; row: FloatingRow; canApplyNow: boolean; onClose: () => void; onDone: () => void; notify: TabProps["notify"] }> = ({ campusId, row, canApplyNow, onClose, onDone, notify }) => {
	const http = HttpManager.getInstance();
	const [destino, setDestino] = useState<number | null>(null);
	const [proj, setProj] = useState<ReentryProjectionDTO | null>(null);
	const [note, setNote] = useState<string>("");
	const [isCaja, setIsCaja] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [insc, setInsc] = useState<number | null>(null);   // null = la promo de inscripción por defecto, 0 = quitada

	useEffect(() => {
		http.enrollmentUpdatesService.options(campusId, row.enrollmentId).then((r) => setIsCaja(r.data.isCaja)).catch(() => { /* sin opciones: solo Por Aplicar */ });
	}, []);
	useEffect(() => {
		if (!destino) { setProj(null); return; }
		let vigente = true;
		setInsc(null);
		http.enrollmentUpdatesService.reentryProjection(campusId, row.enrollmentId, destino)
			.then((r) => { if (vigente) { setProj(r.data); } })
			.catch((e) => vigente && notify(errorText(e), false));
		return () => { vigente = false; };
	}, [destino]);

	const soloIds = useMemo(() => row.openingSchedules.map((s) => s.id), [row]);
	const complemento = useMemo(() => row.openingSchedules.map((s) => filaDeDestino(s, cursoCorto(row.courseName || ""), campusId)), [row, campusId]);
	const detalle = useMemo(() => {
		const out: { [id: number]: DetalleHorario } = {};
		row.openingSchedules.forEach((s) => { out[s.id] = { cupo: s.limit || null, alumnos: s.enrolled, lleno: s.isFull, sesion: s.classesGiven + 1 }; });
		return out;
	}, [row]);

	const create = (applyNow: boolean) => {
		if (!destino) { return; }
		setBusy(true);
		http.enrollmentUpdatesService.create(campusId, { applyNow, items: [{ enrollmentId: row.enrollmentId, typeId: UPDATE_TYPE.REENTRY, toScheduleId: destino, note: note.trim(), promotion: promoDeReingreso(destino, insc) }] })
			.then((r) => { notify(applyNow && !(r.data.updates || []).some((u) => u.statusId === 1) ? S.applied : S.sent(1)); onDone(); })
			.catch((e) => notify(errorText(e), false)).finally(() => setBusy(false));
	};
	const ok = !!destino && note.trim().length >= 3 && !busy;
	return (
		<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={onClose}>
			<div className="wau-modal ancho" onClick={(e) => e.stopPropagation()}>
				<h3>{S.locate}, {row.studentName}</h3>
				<BuscadorHorarios compacto campusId={campusId} soloIds={soloIds} complemento={complemento} detalle={detalle} seleccionado={destino} onElegir={(h) => setDestino(h.id)} />
				{proj ? (
					<div style={{ margin: "10px 0" }}>
						<span className="wau-lab">{S.projection}, {S.sessionsOf(proj.sessionsTaken, proj.sessionsTotal)}, {S.sessionsPending(proj.sessionsPending)}</span>
						<div className="wau-meses wau-num">
							{proj.sessions.slice(0, 40).map((s) => <span key={"ps" + s.sessionNumber} className={"wau-mes" + (s.status === "sin_fecha" ? " vencida" : "")} title={s.label}><b>{s.sessionNumber}</b>{s.date ? fechaCorta(s.date) : "—"}</span>)}
						</div>
					</div>
				) : null}
				<InscripcionReingreso info={proj?.inscription} value={insc} onChange={setInsc} busy={busy} />
				<textarea className="wau-entrada" style={{ marginTop: 10 }} rows={2} placeholder={S.notePlaceholder} aria-label={S.note} value={note} onChange={(e) => setNote(e.target.value)} />
				<div className="wau-acciones">
					<button type="button" className="wau-btn s" onClick={onClose}>{S.cancel}</button>
					{isCaja && canApplyNow ? <button type="button" className="wau-btn p" disabled={!ok} onClick={() => create(true)}>{S.applyNow}</button> : null}
					<button type="button" className={"wau-btn " + (isCaja && canApplyNow ? "s" : "p")} disabled={!ok} onClick={() => create(false)}>{S.leavePending}</button>
				</div>
			</div>
		</div>
	);
};

/** Rango de fechas del filtro (mismo chip que los demás). */
const FiltroFechas: React.FC<{ desde: string; hasta: string; onCambio: (desde: string, hasta: string) => void }> = ({ desde, hasta, onCambio }) => {
	const [abierto, setAbierto] = useState<boolean>(false);
	const raiz = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!abierto) { return; }
		const fuera = (e: MouseEvent) => { if (raiz.current && !raiz.current.contains(e.target as Node)) { setAbierto(false); } };
		document.addEventListener("mousedown", fuera);
		return () => document.removeEventListener("mousedown", fuera);
	}, [abierto]);
	const activo = !!(desde || hasta);
	return (
		<div className="wbh-filtro" ref={raiz}>
			<button type="button" className={"wbh-chip" + (activo ? " on" : "")} aria-expanded={abierto} onClick={() => setAbierto(!abierto)}>
				{S.datesFilter}{activo ? <b>{[desde && fechaCorta(desde), hasta && fechaCorta(hasta)].filter(Boolean).join(" a ")}</b> : <span className="caret" aria-hidden="true">▾</span>}
			</button>
			{activo ? <button type="button" className="wbh-x" aria-label={"Quitar " + S.datesFilter} onClick={() => onCambio("", "")}>×</button> : null}
			{abierto ? (
				<div className="wbh-pop" style={{ padding: 10, display: "grid", gap: 8 }}>
					<label className="wau-campo"><span className="wau-lab">{S.from}</span><input type="date" className="wau-entrada" value={desde} max={hasta || undefined} onChange={(e) => onCambio(e.target.value, hasta)} /></label>
					<label className="wau-campo"><span className="wau-lab">{S.until}</span><input type="date" className="wau-entrada" value={hasta} min={desde || undefined} onChange={(e) => onCambio(desde, e.target.value)} /></label>
				</div>
			) : null}
		</div>
	);
};

/** "Horario completado" a "Horario Completado" (los chips van en Title Case; el servidor manda el texto corrido). */
const titulo = (t: string): string => t.split(" ").map((p, i) => i > 0 && ["de", "del", "la", "el", "en", "a", "y", "o", "con", "por"].includes(p) ? p : p.charAt(0).toUpperCase() + p.slice(1)).join(" ");

interface Plantel { id: number; nombre: string; countryId: number | null; }

const FiltroOrigen: React.FC<{ campusId: number; origen: FiltrosFlotantes["origen"]; onCambio: (o: FiltrosFlotantes["origen"]) => void }> = ({ campusId, origen, onCambio }) => {
	const [eligiendo, setEligiendo] = useState<boolean>(false);
	return (
		<>
			<span className="wbh-filtro">
				<button type="button" className={"wbh-chip" + (origen ? " on" : "")} onClick={() => setEligiendo(true)}>{S.originFilter}{origen ? <b>{origen.nombre}</b> : <span className="caret" aria-hidden="true">▾</span>}</button>
				{origen ? <button type="button" className="wbh-x" aria-label={"Quitar " + S.originFilter} onClick={() => onCambio(null)}>×</button> : null}
			</span>
			{eligiendo ? (
				<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={() => setEligiendo(false)}>
					<div className="wau-modal ancho" onClick={(e) => e.stopPropagation()}>
						<h3>{S.originFilter}</h3>
						<BuscadorHorarios campusId={campusId} autoFocus seleccionado={origen?.id} onElegir={(h) => { onCambio({ id: h.id, nombre: [h.curso, h.horario].filter(Boolean).join(", ") }); setEligiendo(false); }} />
						<div className="wau-acciones"><button type="button" className="wau-btn s" onClick={() => setEligiendo(false)}>{S.cancel}</button></div>
					</div>
				</div>
			) : null}
		</>
	);
};

/** Flotantes (aplazadas y sin horario, de la más reciente a la más vieja) y Por Cerrar (sin deuda en un horario cerrado). Su dinero sigue válido. */
const FlotantesTab: React.FC<TabProps> = ({ campusId, countryId, movil, canApplyNow, notify, changed }) => {
	const veWa = useVeWappChat();
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const planteles: Plantel[] = useMemo(() => (Object.values(campusRef || {}) as any[]).filter((c) => c && c.id)
		.map((c) => ({ id: Number(c.id), nombre: String(c.name || "Plantel " + c.id), countryId: c.company?.country_id ? Number(c.company.country_id) : null }))
		.sort((a, b) => a.nombre.localeCompare(b.nombre, "es")), [campusRef]);
	const nombrePlantel = (id: number) => planteles.find((p) => p.id === id)?.nombre || "";
	const paisDe = (id: number) => planteles.find((p) => p.id === id)?.countryId ?? countryId;

	const [vista, setVista] = useState<FloatingView>("flotantes");
	const [f, setF] = useState<FiltrosFlotantes>(SIN_FILTROS);
	const [page, setPage] = useState<number>(1);
	const [data, setData] = useState<FloatingListDTO | null>(null);
	const [cargando, setCargando] = useState<boolean>(false);
	const [totales, setTotales] = useState<{ flotantes: number; porCerrar: number } | null>(null);
	const [cursos, setCursos] = useState<{ [id: string]: string }>({});
	const [ubicar, setUbicar] = useState<FloatingRow | null>(null);
	const [masivo, setMasivo] = useState<{ campusId: number; ids: number[] } | null>(null);
	const [grupos, setGrupos] = useState<{ campusId: number; ids: number[] }[] | null>(null);
	const [tick, setTick] = useState<number>(0);
	const porCerrar = vista === "por_cerrar";

	const cambia = (parcial: Partial<FiltrosFlotantes>) => { setF((a) => ({ ...a, ...parcial })); setPage(1); };
	const limpiar = () => { setF(SIN_FILTROS); setPage(1); };
	const cambiaVista = (v: FloatingView) => { setVista(v); setF((a) => ({ ...a, dias: [], pago: [], contacto: [] })); setPage(1); };

	useEffect(() => {
		let vigente = true;
		setCargando(true);
		cargarFlotantes(campusId, consulta(vista, f, page))
			.then((r) => { if (vigente) { setData(r); } })
			.catch((e) => { if (vigente) { notify(errorText(e), false); setData((d) => d || { data: [], total: 0, perPage: 0, currentPage: 1, lastPage: 1, counts: { total: 0, byReason: {} }, reasons: {}, view: vista, desde: null }); } })
			.finally(() => vigente && setCargando(false));
		return () => { vigente = false; };
	}, [campusId, vista, f, page, tick]);

	// Conteo de cada vista: al abrir, al cambiar los planteles y cuando algo cambia aquí (nunca por reloj).
	const clavePlanteles = f.planteles.join(",");
	useEffect(() => {
		let vigente = true;
		contarVistas(campusId, f.planteles).then((t) => vigente && setTotales(t)).catch(() => null);
		return () => { vigente = false; };
	}, [campusId, clavePlanteles, tick]);

	// Opciones de Curso: las que han salido en las filas (el servidor filtra por id de curso, que es de cada plantel).
	useEffect(() => {
		if (!data) { return; }
		setCursos((antes) => {
			const sig = { ...antes };
			data.data.forEach((r) => { if (r.courseId && r.courseName) { sig[String(r.courseId)] = cursoCorto(r.courseName) || r.courseName; } });
			return Object.keys(sig).length === Object.keys(antes).length ? antes : sig;
		});
	}, [data]);

	const refrescar = () => setTick((t) => t + 1);
	const alAbrirChat = (r: FloatingRow) => () => {
		marcarContactada(r.campusId, r.enrollmentId).then(refrescar).catch((e) => notify(errorText(e), false));
	};

	const abrirVarios = () => {
		todasLasFilas(campusId, vista, f).then((filas) => {
			const porPlantel = new Map<number, Set<number>>();
			filas.forEach((r) => { const s = porPlantel.get(r.campusId) || new Set<number>(); s.add(r.studentId); porPlantel.set(r.campusId, s); });
			const lista = Array.from(porPlantel.entries()).map(([id, ids]) => ({ campusId: id, ids: Array.from(ids) }));
			if (lista.length === 1) { setMasivo(lista[0]); } else if (lista.length > 1) { setGrupos(lista); }
		}).catch((e) => notify(errorText(e), false));
	};

	if (data === null) { return <div className="wau-caja wau-sec">{S.loading}</div>; }
	const filas = data.data;
	const motivos = Object.keys(data.reasons);
	const dineroDe = (r: FloatingRow, v: number) => dinero(v, paisDe(r.campusId));

	const pagoCelda = (r: FloatingRow) => r.debt.owes ? (
		<div className="wau-oro">
			<b>{r.debt.overdue > 0.005 ? S.overdueOf(dineroDe(r, r.debt.overdue)) : S.balanceOf(dineroDe(r, r.debt.amount))}</b>
			{r.debt.pendingTuitions > 0 ? <div className="small">{S.pendingTuitions(r.debt.pendingTuitions)}</div> : null}
		</div>
	) : <span className="wau-pill ok">{S.upToDate}</span>;
	const contactadaCelda = (r: FloatingRow) => r.contacted?.at
		? <><b>{r.contacted.byName || ""}</b><div className="wau-sec small">{fechaCorta(r.contacted.at)}</div></>
		: <span className="wau-sec">{S.notContacted}</span>;
	const acciones = (r: FloatingRow) => (
		<div className="wau-fila" style={{ gap: 6, flexWrap: "wrap", justifyContent: movil ? "flex-start" : "flex-end" }}>
			<button type="button" className="wau-btn p mini" disabled={!!r.pendingUpdate} onClick={() => setUbicar(r)}>{S.locate}</button>
			<BotonWhatsappAlumno campusId={r.campusId} studentId={r.studentId} nombre={r.studentName} sugerido={r.whatsappText} alAbrir={alAbrirChat(r)} />
		</div>
	);
	const alumna = (r: FloatingRow) => <><b>{r.studentName}</b>{r.phone ? <div className="wau-sec small">{r.phone}</div> : null}</>;
	const cursoCelda = (r: FloatingRow) => <>{cursoCorto(r.courseName || "") || r.courseName}{r.levelName ? <div className="wau-sec small">{r.levelName}</div> : null}</>;
	const queCelda = (r: FloatingRow) => <>{titulo(r.reasonLabel || "")}{r.fromSchedule ? <div className="wau-sec small">{nombreHorario(r.fromSchedule.name)}</div> : null}</>;
	const diasCelda = (r: FloatingRow) => <>{r.date ? <div>{fechaCorta(r.date)}</div> : null}<b className="wau-oro">{r.days ?? 0}</b> <span className="wau-sec small">d</span></>;
	const quien = (r: FloatingRow) => r.causedByName || S.system;
	const sesiones = (r: FloatingRow) => S.sessionsTakenOf(r.sessionsTaken, r.sessionsRequired);

	return (
		<div className="wau-pila wau-flot" style={cargando ? { opacity: .75 } : undefined}>
			<div className="wau-fila" style={{ alignItems: "center" }}>
				<div className="wau-seg" role="group">
					<button type="button" aria-pressed={!porCerrar} onClick={() => cambiaVista("flotantes")}>{S.flotantes}{totales ? <span className="n">{totales.flotantes}</span> : null}</button>
					<button type="button" aria-pressed={porCerrar} onClick={() => cambiaVista("por_cerrar")}>{S.porCerrar}{totales ? <span className="n">{totales.porCerrar}</span> : null}</button>
				</div>
				{!porCerrar && veWa && data.total > 0 ? <button type="button" className="wau-btn s mini" style={{ marginLeft: "auto" }} onClick={abrirVarios}>{S.whatsappVarias}</button> : null}
			</div>
			<div className="wau-chips" style={{ marginBottom: 0 }} role="group" aria-label={S.whatHappened}>
				{motivos.map((m) => (
					<button key={m} type="button" className={"wbh-chip" + (f.motivos.includes(m) ? " on" : "")} aria-pressed={f.motivos.includes(m)}
						style={!data.counts.byReason[m] && !f.motivos.includes(m) ? { opacity: .6 } : undefined}
						onClick={() => cambia({ motivos: f.motivos.includes(m) ? f.motivos.filter((x) => x !== m) : [...f.motivos, m] })}>
						{titulo(data.reasons[m])} <b className="wau-num">{data.counts.byReason[m] || 0}</b>
					</button>
				))}
			</div>
			<div className="wau-chips">
				{planteles.length > 1 ? <FiltroCasillas etiqueta={S.campusFilter} opciones={planteles.map((p) => ({ v: String(p.id), t: p.nombre }))} elegidos={f.planteles} onCambio={(v) => cambia({ planteles: v, curso: [] })} /> : null}
				<FiltroCasillas unico etiqueta={S.courseFilter} opciones={Object.keys(cursos).map((id) => ({ v: id, t: cursos[id] })).sort((a, b) => a.t.localeCompare(b.t, "es"))} elegidos={f.curso} onCambio={(v) => cambia({ curso: v })} />
				<FiltroOrigen campusId={campusId} origen={f.origen} onCambio={(o) => cambia({ origen: o })} />
				<FiltroFechas desde={f.desde} hasta={f.hasta} onCambio={(d, h) => cambia({ desde: d, hasta: h })} />
				{!porCerrar ? (
					<>
						<FiltroCasillas unico etiqueta={S.daysFilter} opciones={[7, 14, 30].map((n) => ({ v: String(n), t: S.moreThan(n) }))} elegidos={f.dias} onCambio={(v) => cambia({ dias: v })} />
						<FiltroCasillas unico etiqueta={S.owesFilter} opciones={[{ v: "debe", t: S.owes }, { v: "al_corriente", t: S.upToDate }]} elegidos={f.pago} onCambio={(v) => cambia({ pago: v })} />
						<FiltroCasillas unico etiqueta={S.contactFilter} opciones={[{ v: "contactada", t: S.contacted }, { v: "sin_contactar", t: S.notContacted }]} elegidos={f.contacto} onCambio={(v) => cambia({ contacto: v })} />
					</>
				) : null}
				{hayFiltros(f) ? <button type="button" className="wbh-limpiar" onClick={limpiar}>{S.removeFilters}</button> : null}
			</div>

			<div className="wau-caja">
				{filas.length === 0 ? <div className="wau-sec">{S.empty}</div> : movil ? filas.map((r) => (
					<div className="wau-tarjeta" key={"f" + r.enrollmentId}>
						<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><div>{alumna(r)}</div><span className="wau-num" style={{ whiteSpace: "nowrap" }}><b className="wau-oro">{r.days ?? 0}</b> d</span></div>
						<div className="linea">{cursoCelda(r)}</div>
						<div style={{ marginTop: 6 }}>{queCelda(r)}</div>
						<div className="linea">{r.date ? fechaCorta(r.date) : ""}{!porCerrar ? ", " + quien(r) : ""}</div>
						{porCerrar ? <div className="linea">{S.flot.sessions} {sesiones(r)}</div> : (
							<>
								<div style={{ margin: "6px 0" }}>{pagoCelda(r)}</div>
								<div className="linea" style={{ marginBottom: 6 }}>{contactadaCelda(r)}</div>
								<div style={{ margin: "6px 0" }}><Aperturas list={r.openingSchedules} /></div>
								{acciones(r)}
							</>
						)}
					</div>
				)) : porCerrar ? (
					<div className="wau-tabla">
						<table>
							<thead><tr><th style={{ width: "26%" }}>{S.flot.student}</th><th style={{ width: "22%" }}>{S.flot.course}</th><th style={{ width: "24%" }}>{S.flot.what}</th><th style={{ width: "14%" }}>{S.flot.endDate}</th><th style={{ width: "14%" }}>{S.flot.sessions}</th></tr></thead>
							<tbody className="wau-num">
								{filas.map((r) => (
									<tr key={"f" + r.enrollmentId}>
										<td>{alumna(r)}</td><td>{cursoCelda(r)}</td><td>{queCelda(r)}</td><td>{diasCelda(r)}</td><td>{sesiones(r)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<div className="wau-tabla">
						<table>
							<thead><tr>
								<th style={{ width: "14%" }}>{S.flot.student}</th><th style={{ width: "12%" }}>{S.flot.course}</th><th style={{ width: "13%" }}>{S.flot.what}</th><th style={{ width: "9%" }}>{S.flot.dateDays}</th>
								<th style={{ width: "8%" }}>{S.flot.who}</th><th style={{ width: "12%" }}>{S.flot.pay}</th><th style={{ width: "10%" }}>{S.flot.contacted}</th><th style={{ width: "12%" }}>{S.flot.opening}</th><th style={{ width: "10%" }} />
							</tr></thead>
							<tbody className="wau-num">
								{filas.map((r) => (
									<tr key={"f" + r.enrollmentId}>
										<td>{alumna(r)}</td><td>{cursoCelda(r)}</td><td>{queCelda(r)}</td><td>{diasCelda(r)}</td><td>{quien(r)}</td>
										<td>{pagoCelda(r)}</td><td>{contactadaCelda(r)}</td><td><Aperturas list={r.openingSchedules} /></td><td className="der">{acciones(r)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{data.lastPage > 1 ? (
				<div className="wau-acciones" style={{ justifyContent: "center" }}>
					<button type="button" className="wau-btn s mini" disabled={page <= 1} aria-label={S.prev} onClick={() => setPage(page - 1)}>←</button>
					<span className="wau-num wau-sec">{page} / {data.lastPage}</span>
					<button type="button" className="wau-btn s mini" disabled={page >= data.lastPage} aria-label={S.next} onClick={() => setPage(page + 1)}>→</button>
				</div>
			) : null}

			{grupos ? (
				<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={() => setGrupos(null)}>
					<div className="wau-modal" onClick={(e) => e.stopPropagation()}>
						<h3>{S.whatsappVarias}</h3>
						<div className="wau-opciones">
							{grupos.map((g) => <button key={"g" + g.campusId} type="button" className="wau-btn s" onClick={() => { setMasivo(g); setGrupos(null); }}>{S.byCampus(nombrePlantel(g.campusId) || "Plantel " + g.campusId, g.ids.length)}</button>)}
						</div>
						<div className="wau-acciones"><button type="button" className="wau-btn s" onClick={() => setGrupos(null)}>{S.cancel}</button></div>
					</div>
				</div>
			) : null}
			{masivo ? <EnvioMasivo campusId={masivo.campusId} studentIds={masivo.ids} origen="flotantes" onClose={() => setMasivo(null)} /> : null}
			{ubicar ? <UbicarModal campusId={ubicar.campusId} row={ubicar} canApplyNow={canApplyNow} notify={notify} onClose={() => setUbicar(null)} onDone={() => { setUbicar(null); refrescar(); changed(); }} /> : null}
		</div>
	);
};

export default FlotantesTab;
